// Regras puras da tela `/app/splits` (WEB-123): rótulo e tom de status, leitura do
// `kind`/`value`, cota e as mensagens de erro do formulário de autorização. Sem React, para o
// teste rodar sem provider nenhum (`spec/17` §11.1).

import { isApiError } from '../../api/errors.ts';
import type { Split, SplitQuota, SplitStatus } from '../../api/queries/splits.ts';
import type { Mode } from '../../app/mode.tsx';
import { formatCents } from '../../lib/money.ts';
import { formatBps } from '../account/model.ts';

/** Mesmo vocabulário de tom das linhas pião de Webhooks (`features/webhooks/model.ts`). */
export type SplitTone = 'ok' | 'bad' | 'wait' | 'neutral';

export const SPLIT_STATUS: Record<SplitStatus, { label: string; tone: SplitTone }> = {
  pending: { label: 'Pendente', tone: 'wait' },
  settled: { label: 'Liquidado', tone: 'ok' },
  reversed: { label: 'Revertido', tone: 'bad' },
  cancelled: { label: 'Cancelado', tone: 'neutral' },
};

/** Ordem do filtro de status: a do ciclo de vida (`19` §4.8). */
export const SPLIT_STATUSES: SplitStatus[] = ['pending', 'settled', 'reversed', 'cancelled'];

export function isSplitStatus(value: string): value is SplitStatus {
  return SPLIT_STATUSES.some((status) => status === value);
}

/**
 * Variante do `Badge` por tom: o selo é neutro e quem diz o estado é o quadrado. Split
 * liquidado é dinheiro que andou de fato, então leva o ouro de `paid` (como o "Pago").
 */
export const TONE_BADGE = {
  ok: 'paid',
  bad: 'destructive',
  wait: 'warning',
  neutral: 'secondary',
} as const satisfies Record<SplitTone, string>;

/**
 * `percent` é bps sobre o líquido do pagador (`19` §4.2); `fixed` é centavos. A mesma grafia
 * de percentual da tela de Taxas (`formatBps`), para não haver duas na mesma conta.
 */
export function splitKindLabel(split: Pick<Split, 'kind' | 'value'>): string {
  return split.kind === 'percent'
    ? `${formatBps(split.value)} do líquido`
    : `${formatCents(split.value)} fixo`;
}

/**
 * Valor que a linha mostra: o liquidado quando existe; antes do `paid` a API devolve `null`
 * (a projeção é do pagador, `19` §5.1) e a linha diz isso em vez de inventar um número.
 */
export function splitAmountLabel(split: Pick<Split, 'amount' | 'status'>): string {
  if (split.amount !== null) {
    return formatCents(split.amount);
  }
  return split.status === 'cancelled' ? 'não liquidou' : 'a liquidar';
}

/** Data de referência da linha: quando liquidou; se não liquidou, quando nasceu. */
export function splitWhen(split: Pick<Split, 'settled_at' | 'created_at'>): string {
  return split.settled_at ?? split.created_at;
}

export interface QuotaView {
  /** Cota zero: envio de split desligado para esta conta (seed de prod, `19` §4.10). */
  blocked: boolean;
  /** 0 a 100, inteiro, para a barra. */
  percent: number;
  label: string;
  sub: string;
}

/**
 * Cota a partir da qual o teto não aparece para ninguém: prod foi liberado sem limite
 * prático em 24/09/2026 (R$ 1 trilhão), e "R$ 0,00 de R$ 1.000.000.000.000,00" é ruído.
 */
export const UNLIMITED_QUOTA_CENTS = 10_000_000_000_000;

export function isUnlimitedQuota(quotaCents: number): boolean {
  return quotaCents >= UNLIMITED_QUOTA_CENTS;
}

/** A cota é por modo (`19` §4.4, janela "por `mode`"): o card diz de qual está falando. */
export function quotaView(quota: SplitQuota, mode: Mode): QuotaView {
  const source = quota.source === 'seller' ? 'cota da sua conta' : 'cota padrão';
  const where = mode === 'live' ? 'em Produção' : 'em Teste';
  if (quota.quota_cents <= 0) {
    return {
      blocked: true,
      percent: 0,
      label: 'Envio desligado',
      sub: `Sua cota de split ${where} é R$ 0,00. Fale com o suporte para liberar.`,
    };
  }
  // Arredonda para CIMA: qualquer uso aparece como um fio na barra, em vez de 0 % mudo.
  const percent = Math.min(100, Math.ceil((quota.used_cents * 100) / quota.quota_cents));
  return {
    blocked: false,
    percent,
    label: `${formatCents(quota.used_cents)} de ${formatCents(quota.quota_cents)}`,
    sub: `${source} · ${quota.window_days} dias corridos ${where}`,
  };
}

/**
 * Mensagem do formulário "Autorizar e-mail", por `code` (`19` §5.3). Nenhuma delas diz se
 * existe conta com o e-mail: `split_authorization_exists` fala da lista do próprio seller.
 */
export function authorizationErrorMessage(error: unknown): string {
  if (!isApiError(error)) {
    return 'Não deu para salvar. Confira a conexão e tente de novo.';
  }
  switch (error.code) {
    case 'validation_failed':
      return 'Digite um e-mail válido.';
    case 'split_authorization_self':
      return 'Esse é o e-mail da sua própria conta.';
    case 'split_authorization_exists':
      return 'Este e-mail já está na sua lista.';
    case 'rate_limited':
      return error.retryAfterS === undefined
        ? 'Muitas tentativas seguidas. Espere um minuto e tente de novo.'
        : `Muitas tentativas seguidas. Tente de novo em ${error.retryAfterS}s.`;
    default:
      return 'Não deu para salvar. Tente de novo em instantes.';
  }
}

/** Texto do sucesso, o MESMO exista ou não a conta (`19` §4.1, anti-enumeração). */
export const AUTHORIZED_MESSAGE =
  'Se existir uma conta LeztyPay com este e-mail, ela já pode enviar split para você';

/**
 * Aviso do split revertido na ponta do recebedor. A visão pública do split não traz
 * `reversed_reason` (só a admin traz), e reversão em produção é sempre MED: a Dotfy não tem
 * estorno (`19` §4.7), o ramo de refund só roda no sandbox. "Questões abertas" de WEB-123.
 */
export const REVERSED_NOTICE =
  'Este valor foi devolvido ao pagador por disputa perdida; se você já sacou, seu saldo pode ficar negativo e o saque fica travado até regularizar';
